import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
import NearestLocationForm from '../components/Location/NearestLocationField';
import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
import Alerts from '../components/Profile&Settings/AlertsSettings';
import { useAuthContext } from '../hooks/useAuthContext';
import InfoPopup from "../components/InfoPopup";
import "./ProfileUpdate.css";
import "./Onboarding.css";
import SkipConfirmationModal from '../components/SkipConfirmationModal';
import firebase from '../Firebase';
import useOnboardingAnalytics from '../hooks/useOnboardingAnalytics';

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [profileInfo, setProfileInfo] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [photosInfo, setPhotosInfo] = useState(null);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [communityProfileInfo, setCommunityProfileInfo] = useState([]);
  const [communityNames, setCommunityNames] = useState([]);
  const [actualCommunityNames, setActualCommunityNames] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [skipModal, setSkipModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [skipModalContent, setSkipModalContent] = useState(null);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  // Refs to each community-profile step's DynamicFormMain, so we can ask
  // "are you actually complete?" synchronously when Continue is clicked,
  // instead of relying on communityProfileInfo (which lags behind by a few
  // renders right after a field auto-saves on blur).
  const communityProfileRefs = useRef([]);

  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Restore step after returning from Didit KYC redirect
  useEffect(() => {
    const savedStep = localStorage.getItem('kycOnboardingReturnStep');
    if (savedStep) {
      setStep(parseInt(savedStep, 10));
      localStorage.removeItem('kycOnboardingReturnStep');
    }
  }, []);

  // Show loading message only after a delay to handle timing issues
  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        setShowLoadingMessage(true);
      }, 2000); // Wait 2 seconds before showing loading message
      
      return () => clearTimeout(timer);
    } else {
      setShowLoadingMessage(false);
    }
  }, [user]);

  // Fetch actual community names from their IDs
  useEffect(() => {
    const fetchCommunityNames = async () => {
      if (user?.listOfCommunities?.length > 0) {
        try {
          const names = [];
          for (const communityId of user.listOfCommunities) {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities/${communityId}`);
            if (response.ok) {
              const community = await response.json();
              names.push(community.title || community.name || 'Community');
            } else {
              names.push('Community');
            }
          }
          setActualCommunityNames(names);
          console.log('[Onboarding] Fetched community names:', names);
        } catch (error) {
          console.error('[Onboarding] Error fetching community names:', error);
          setActualCommunityNames(['Community']);
        }
      }
    };

    fetchCommunityNames();
  }, [user?.listOfCommunities]);

  const totalCommunities = user?.listOfCommunities?.length || 0;
  // 1: profile basics, 2: location, [community steps], photos, notifications, finish
  const totalSteps = totalCommunities + 5;

  // Where people get to and what stops them (admin: onboardingviews).
  const track = useOnboardingAnalytics({
    userId: user?.userId,
    communityId: user?.listOfCommunities?.[totalCommunities - 1],
    totalSteps,
  });
  const stepName = useCallback((n) => {
    if (n === 1) return 'basics';
    if (n === 2) return 'location';
    if (n > 2 && n <= totalCommunities + 2) return 'community_profile';
    if (n === totalCommunities + 3) return 'photo';
    if (n === totalCommunities + 4) return 'notifications';
    return 'finish';
  }, [totalCommunities]);
  useEffect(() => { track('step_shown', `${step}:${stepName(step)}`); }, [step, stepName, track]);
  useEffect(() => { if (photosUploading) track('photo_uploading'); }, [photosUploading, track]);
  useEffect(() => { if (photosInfo) track('photo_added'); }, [photosInfo, track]);
  useEffect(() => { if (isChecked) track('alerts_ticked'); }, [isChecked, track]);

  // Memoized function to prevent infinite loop in DynamicFormMain
  const handleCommunityProfileComplete = useCallback((index, complete) => {
    setCommunityProfileInfo(prev => {
      const updated = [...prev];
      updated[index] = complete;
      return updated;
    });
  }, []);

  // Check a community-profile step's completeness: prefer the ref's
  // synchronous, always-current check; fall back to the (laggier) state flag
  // if the ref isn't mounted yet for some reason.
  const isCommunityProfileStepComplete = useCallback((index) => {
    const refCheck = communityProfileRefs.current[index]?.checkCompletionNow?.();
    if (refCheck) return refCheck.isComplete;
    return communityProfileInfo[index];
  }, [communityProfileInfo]);

  const isStepComplete = useCallback(() => {
    switch(step) {
      case 1: return !!profileInfo;
      case 2: return !!locationInfo;
      case 3: return isCommunityProfileStepComplete(0);
      default:
        if (step > 3 && step <= totalCommunities + 2) return isCommunityProfileStepComplete(step - 3);
        // Photos step — block advancing while a photo is still mid-upload —
        // don't let the user navigate away from an in-flight upload (which
        // used to be how a still-uploading blob preview could end up treated
        // as "done").
        if (step === totalCommunities + 3) return !!photosInfo && !photosUploading;
        if (step === totalCommunities + 4) return true; // Notifications step is always complete if reached
        return true;
    }
  }, [step, profileInfo, locationInfo, photosInfo, photosUploading, isCommunityProfileStepComplete, totalCommunities]);

  useEffect(() => {
    if (step === totalSteps - 1) {
      // Skip the congratulations step and go directly to messenger
      // Get the most recently joined community (last in the list)
      const latestCommunityId = user?.listOfCommunities?.[user.listOfCommunities.length - 1];
      const navigationUrl = latestCommunityId 
        ? `/messenger?from=onboarding&communityId=${latestCommunityId}#messenger-chatbox`
        : '/messenger?from=onboarding#messenger-chatbox';

      track('completed');
      navigate(navigationUrl);
    }
  }, [step, totalSteps, navigate, user, track]);

  const handleCommunityName = (index, name) => {
    setCommunityNames(prev => {
      const newNames = [...prev];
      newNames[index] = name;
      return newNames;
    });
  };

  const handleNext = () => {
    const blocked = () => track('continue_blocked', stepName(step));
    // Special handling for Alerts step
    if (step === totalCommunities + 4 && !isChecked) {
      setSkipModalContent({
        title: 'Email notifications required for an active account',
        message: 'Because we are just a website, we rely on alerting you to messages by emailing you.',
        cancelText: 'Go Back',
        confirmText: 'Skip'
      });
      blocked();
      setSkipModal(true);
      return;
    }

    // Photo still uploading: no "Skip for Now" escape hatch here — skipping
    // would navigate away mid-upload, exactly what this guard exists to
    // prevent. Just ask them to wait a moment.
    if (step === totalCommunities + 3 && photosUploading) {
      setSkipModalContent({
        title: 'Still uploading…',
        message: 'Your photo is still being saved — give it a moment and try again.',
        cancelText: 'OK',
        confirmText: ''
      });
      blocked();
      setSkipModal(true);
      return;
    }

    // Skipping the photo: offer their automatic avatar for now — a real
    // photo is needed before they're put in touch with anyone.
    if (step === totalCommunities + 3 && !isStepComplete() && user?.userId) {
      setSkipModalContent({
        kind: 'avatar',
        title: 'Add a photo of yourself?',
        message: (
          <>
            <img
              src={`${process.env.REACT_APP_API_BASE_URL}/avatar/v2/${user.userId}.png?preview=1`}
              alt=""
              style={{ display: 'block', width: 120, height: 120, borderRadius: 12, margin: '0.6rem auto 0.8rem' }}
            />
            You&apos;ll need a photo of yourself before you&apos;re put in touch with members. For now, we can use this image
            instead — add your own any time from your Profile.
          </>
        ),
        cancelText: 'Add a photo',
        confirmText: 'Use this for now',
      });
      blocked();
      setSkipModal(true);
      return;
    }

    if (!isStepComplete()) {
      setSkipModalContent(null); // Use default content
      blocked();
      setSkipModal(true);
    } else {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleSkipConfirm = async () => {
    track(skipModalContent?.kind === 'avatar' ? 'avatar_used' : 'skip_confirmed', stepName(step));
    if (skipModalContent?.kind === 'avatar') {
      // "Use this for now": save their automatic avatar as their picture.
      try {
        const token = await firebase.auth().currentUser?.getIdToken();
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/avatar/use`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        window.dispatchEvent(new Event('ic:pics-updated'));
      } catch (err) {
        console.error('[onboarding] avatar:', err);
      }
    }
    setSkipModal(false);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleSkipCancel = () => {
    track('skip_cancelled', stepName(step));
    setSkipModal(false);
  };

  const renderStepContent = () => {
    // Handle authentication loading
    if (!user) {
      if (showLoadingMessage) {
        return (
          <div className="loading-message" style={{textAlign: 'center', padding: '2rem'}}>
            <h3>Loading your profile...</h3>
            <p>If this takes too long, you may need to <a href="/login?verified=true&continueUrl=%2FOnboarding">log in again</a>.</p> 
            <p><small>This can happen if there are Firebase authentication issues.</small></p>
          </div>
        );
      } else {
        // Show a minimal loading state without the warning message initially
        return (
          <div className="loading-message" style={{textAlign: 'center', padding: '2rem'}}>
            <h3>Setting up your profile...</h3>
            <div style={{marginTop: '1rem'}}>
              <div className="spinner" style={{
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #007bff', 
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        );
      }
    }


    switch (step) {
      case 1:
        return (
          <div>
            <h1 className="MainTitle">
              Welcome to {actualCommunityNames[0] || 'Our Community'}! <span role="img" aria-label="wave">👋</span>
              <InfoPopup infoText={
                <>
                  <p>Let's get you set up. We'll start with some basic information to help others get to know you.</p>
                  <p><em>This information will be visible on your public profile.</em></p>
                </>
              } />
            </h1>
            <ProfileBasics setProfileInfo={setProfileInfo} hideAgeVerify={true} />
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="step-title">
              Your Local Area
              <InfoPopup infoText={
                <>
                  <p>Sharing your location helps us connect you with nearby members and relevant local groups.</p>
                  <p><em>We only store approximate location data and never share exact coordinates.</em></p>
                </>
              } />
            </h2>
            <NearestLocationForm setLocationInfo={setLocationInfo} />
          </div>
        );

      default:
        if (step > 2 && step <= totalCommunities + 2) {
          const index = step - 3;
          const community = user.listOfCommunities[index];
          return (
            <div>
              <h2 className="step-title">
                {communityNames[index] || community?.name || `Community Profile`}
                <InfoPopup infoText={
                  <>
                    <p>Customize your profile for this specific community. This information will only be visible to members of this group.</p>
                    <p><em>You can update these settings anytime from your profile page.</em></p>
                  </>
                } />
              </h2>
              <DynamicFormMain
                ref={(el) => { communityProfileRefs.current[index] = el; }}
                user={user}
                communityId={community._id || community}
                onCommunityName={(name) => handleCommunityName(index, name)}
                setCommunityProfileComplete={(complete) => handleCommunityProfileComplete(index, complete)}
                mode={"Profile"}
                autoSaveOnBlur={true}
              />
            </div>
          );
        }

        // Photos step — now the last thing the user actively completes,
        // right before notifications/finish.
        if (step === totalCommunities + 3) {
          return (
            <div>
              <h2 className="step-title">
                Profile Photos
                <InfoPopup infoText={
                  <>
                    <p>Add photos to help others recognize you. The first photo will be your main profile image.</p>
                    <p><em>Please ensure photos clearly show your face and meet our community guidelines.</em></p>
                  </>
                } />
              </h2>
              <ProfilePicturesGrid setPhotosInfo={setPhotosInfo} setPhotosUploading={setPhotosUploading} />
            </div>
          );
        }

        // Notifications step
        if (step === totalCommunities + 4) {
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                <h2 className="step-title">
                  Notification Preferences
                </h2>
                <InfoPopup
                  infoText={
                    <ul style={{
                      margin: 0,
                      paddingLeft: '1.2em',
                      color: 'var(--text-light)',
                      fontSize: '1em',
                      lineHeight: 1.6
                    }}>
                      <li>We use your email to alert you to messages from other memembers and to keep you updated with settings and preferences</li>
                      <li>Your personal email address won't be shared with other members.</li>
                      <li>You can turn off at anytime.</li>
                    </ul>
                  }
                />
              </div>
              <Alerts 
                isChecked={isChecked} 
                onCheckboxChange={setIsChecked}
              />
            </div>
          );
        }

        // Final "Finish Setup" step
        if (step === totalSteps) {
          return (
            <div className="completion-screen text-center">
              <h1 className="congratulations-title">Setup Complete! <span role="img" aria-label="party">🎉</span></h1>
              <div className="completion-message">
                <p>Welcome to our community! You're now ready to start connecting with others.</p>
                <p>Redirecting you to the main dashboard...</p>
              </div>
            </div>
          );
        }
        return null;
    }
  };

  return (
    <div className="container profile-update-container onboarding-card">
      {/* Progress Bar */}
      <div className="progress-container mb-2">
        <div className="progress-text">
          Step {Math.min(step, totalSteps)} of {totalSteps}
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <SwitchTransition>
        <CSSTransition
          key={step}
          timeout={300}
          classNames="fade-step"
        >
          <div className="step-container">
            {renderStepContent()}
            {step < totalSteps - 1 && (
              <div className="buttonContainer mt-2">
                <button
                  className="button next-button"
                  onClick={handleNext}
                >
                  {step === totalSteps - 1 ? 'Complete Setup' : 'Continue'}
                </button>
              </div>
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>

      {/* Skip Confirmation Modal */}
      <SkipConfirmationModal
        isOpen={skipModal}
        onCancel={handleSkipCancel}
        onConfirm={handleSkipConfirm}
        content={skipModalContent}
      />

      {/* Email Alerts Check Modal */}
      {showModal && (
        <EmailAlertsCheck
          onClose={() => setShowModal(false)}
          onConfirm={() => {
            setShowModal(false);
            setStep(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default Onboarding;


// import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import InfoPopup from "../components/InfoPopup";
// import "./ProfileUpdate.css";
// import "./Onboarding.css";
// import SkipConfirmationModal from '../components/SkipConfirmationModal';

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [profileInfo, setProfileInfo] = useState(null);
//   const [locationInfo, setLocationInfo] = useState(null);
//   const [photosInfo, setPhotosInfo] = useState(null);
//   const [communityProfileInfo, setCommunityProfileInfo] = useState([]);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [skipModal, setSkipModal] = useState(false);
//   const [isChecked, setIsChecked] = useState(false);
//   const [skipModalContent, setSkipModalContent] = useState(null);

//   const { user } = useAuthContext();
//   const navigate = useNavigate();

//   const totalCommunities = user?.listOfCommunities?.length || 0;
//   const totalSteps = totalCommunities + 6;

//   const isStepComplete = useCallback(() => {
//     switch(step) {
//       case 1: return !!profileInfo;
//       case 2: return !!locationInfo;
//       case 3: return !!photosInfo;
//       case 4: return communityProfileInfo[0];
//       default: 
//         if (step > 4 && step <= totalCommunities + 3) return communityProfileInfo[step - 4];
//         return true;
//     }
//   }, [step, profileInfo, locationInfo, photosInfo, communityProfileInfo, totalCommunities]);

//   useEffect(() => {
//     if (step === totalSteps) {
//       setTimeout(() => navigate('/messenger'), 4000);
//     }
//   }, [step, totalSteps, navigate]);

//   const handleCommunityName = (index, name) => {
//     setCommunityNames(prev => {
//       const newNames = [...prev];
//       newNames[index] = name;
//       return newNames;
//     });
//   };

//   const handleNext = () => {
//     // Special handling for Alerts step
//     if (step === totalCommunities + 4 && !isChecked) {
//       setSkipModalContent({
//         title: 'Email notifictions required for an active account',
//         message: 'Because we are just a website, we rely on alerting you to messages by emailing you',
//         cancelText: 'Go Back',
//         confirmText: 'Skip'
//       });
//       setSkipModal(true);
//       return;
//     }
    
//     if (!isStepComplete()) {
//       setSkipModalContent(null); // Use default content
//       setSkipModal(true);
//     } else {
//       setStep(prev => Math.min(prev + 1, totalSteps));
//     }
//   };

//   const handleSkipConfirm = () => {
//     setSkipModal(false);
//     setStep(prev => Math.min(prev + 1, totalSteps));
//   };

//   const handleSkipCancel = () => {
//     setSkipModal(false);
//   };

//   const renderStepContent = () => {
//     if (!user) return <div className="loading-message">Loading your profile...</div>;

//     switch (step) {
//       case 1:
//         return (
//           <div>
//             <h1 className="MainTitle">
//               Welcome to Our Community! <span role="img" aria-label="wave">👋</span>
//               <InfoPopup infoText={
//                 <>
//                   <p>Let's get you set up. We'll start with some basic information to help others get to know you.</p>
//                   <p><em>This information will be visible on your public profile.</em></p>
//                 </>
//               } />
//             </h1>
//             <ProfileBasics setProfileInfo={setProfileInfo} />
//           </div>
//         );

//       case 2:
//         return (
//           <div>
//             <h2 className="step-title">
//               Your Local Area
//               <InfoPopup infoText={
//                 <>
//                   <p>Sharing your location helps us connect you with nearby members and relevant local groups.</p>
//                   <p><em>We only store approximate location data and never share exact coordinates.</em></p>
//                 </>
//               } />
//             </h2>
//             <NearestLocationForm setLocationInfo={setLocationInfo} />
//           </div>
//         );

//       case 3:
//         return (
//           <div>
//             <h2 className="step-title">
//               Profile Photos
//               <InfoPopup infoText={
//                 <>
//                   <p>Add photos to help others recognize you. The first photo will be your main profile image.</p>
//                   <p><em>Please ensure photos clearly show your face and meet our community guidelines.</em></p>
//                 </>
//               } />
//             </h2>
//             <ProfilePicturesGrid setPhotosInfo={setPhotosInfo} />
//           </div>
//         );

//       default:
//         if (step > 3 && step <= totalCommunities + 3) {
//           const index = step - 4;
//           const community = user.listOfCommunities[index];
//           return (
//             <div>
//               <h2 className="step-title">
//                 {communityNames[index] || community?.name || `Community Profile`}
//                 <InfoPopup infoText={
//                   <>
//                     <p>Customize your profile for this specific community. This information will only be visible to members of this group.</p>
//                     <p><em>You can update these settings anytime from your profile page.</em></p>
//                   </>
//                 } />
//               </h2>
//               <CommunityProfileMain
//                 user={user}
//                 communityId={community._id || community}
//                 onCommunityName={(name) => handleCommunityName(index, name)}
//                 setCommunityProfileComplete={(complete) => {
//                   setCommunityProfileInfo(prev => {
//                     const updated = [...prev];
//                     updated[index] = complete;
//                     return updated;
//                   });
//                 }}
//               />
//             </div>
//           );
//         }

// if (step === totalCommunities + 4) {
//   return (
//     <div>
//       <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
//         <h2 className="step-title">
//           Notification Preferences
//         </h2>
//         <InfoPopup
//           infoText={
//             <ul style={{
//               margin: 0,
//               paddingLeft: '1.2em',
//               color: 'var(--text-light)',
//               fontSize: '1em',
//               lineHeight: 1.6
//             }}>
//               <li>You'll only be contacted about people to connect with and people you've chosen to connect with.</li>
//               <li>Your personal email address won't be shared with other members.</li>
//               <li>You can turn off at anytime.</li>
//             </ul>
//           }
//         />
//       </div>
//       <Alerts 
//         isChecked={isChecked} 
//         onCheckboxChange={setIsChecked}
//       />
//     </div>
//   );
// }

 
//         if (step === totalCommunities + 5) {
//           return (
//             <div>
//               <h2 className="step-title">
//                 Final Settings
//                 <InfoPopup infoText={
//                   <>
//                     <p>Review and confirm your privacy preferences and community settings.</p>
//                     <p><em>These settings help us protect your privacy and customize your experience.</em></p>
//                   </>
//                 } />
//               </h2>
//               <CommuniSettings
//                 user={user}
//                 communityId={user.listOfCommunities[totalCommunities - 1]}
//               />
//             </div>
//           );
//         }

//         if (step === totalSteps) {
//           return (
//             <div className="completion-screen text-center">
//               <h1 className="congratulations-title">Setup Complete! <span role="img" aria-label="party">🎉</span></h1>
//               <div className="completion-message">
//                 <p>Welcome to our community! You're now ready to start connecting with others.</p>
//                 <p>Redirecting you to the main dashboard...</p>
//               </div>
//             </div>
//           );
//         }
//         return null;
//     }
//   };

//   return (
//     <div className="container profile-update-container onboarding-card">
//       {/* Progress Bar */}
//       <div className="progress-container mb-2">
//         <div className="progress-text">
//           Step {Math.min(step, totalSteps)} of {totalSteps}
//         </div>
//         <div className="progress-bar">
//           <div 
//             className="progress-fill"
//             style={{ width: `${(step / totalSteps) * 100}%` }}
//           />
//         </div>
//       </div>

//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           timeout={300}
//           classNames="fade-step"
//         >
//           <div className="step-container">
//             {renderStepContent()}
//             {step < totalSteps && (
//               <div className="buttonContainer mt-2">
//                 <button 
//                   className="button next-button"
//                   onClick={handleNext}
//                 >
//                   {step === totalSteps - 1 ? 'Complete Setup' : 'Continue'}
//                 </button>
//               </div>
//             )}
//           </div>
//         </CSSTransition>
//       </SwitchTransition>

//       {/* Skip Confirmation Modal */}
//       <SkipConfirmationModal
//         isOpen={skipModal}
//         onCancel={handleSkipCancel}
//         onConfirm={handleSkipConfirm}
//         content={skipModalContent}
//       />

//       {/* Email Alerts Check Modal */}
//       {showModal && (
//         <EmailAlertsCheck
//           onClose={() => setShowModal(false)}
//           onConfirm={() => {
//             setShowModal(false);
//             setStep(prev => prev + 1);
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default Onboarding;

// import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import "./Settings.css";
// import "./ProfileUpdate.css";
// import "./Onboarding.css";
 
// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [profileInfo, setProfileInfo] = useState(null);
//   const [locationInfo, setLocationInfo] = useState(null);
//   const [photosInfo, setPhotosInfo] = useState(null);
//   const [communityProfileInfo, setCommunityProfileInfo] = useState([]);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [skipModal, setSkipModal] = useState(false);
//   const [isChecked, setIsChecked] = useState(false);
  
//   const { user } = useAuthContext();
//   const navigate = useNavigate();

//   const totalCommunities = user?.listOfCommunities?.length || 0;
//   const totalSteps = totalCommunities + 6;

//   const isStepComplete = useCallback(() => {
//     switch(step) {
//       case 1: return !!profileInfo;
//       case 2: return !!locationInfo;
//       case 3: return !!photosInfo;
//       case 4: return communityProfileInfo[0];
//       default: 
//         if (step > 4 && step <= totalCommunities + 3) return communityProfileInfo[step - 4];
//         return true;
//     }
//   }, [step, profileInfo, locationInfo, photosInfo, communityProfileInfo, totalCommunities]);

//   useEffect(() => {
//     if (step === totalSteps) {
//       setTimeout(() => navigate('/tester1'), 4000);
//     }
//   }, [step, totalSteps, navigate]);

//   const handleCommunityName = (index, name) => {
//     setCommunityNames(prev => {
//       const newNames = [...prev];
//       newNames[index] = name;
//       return newNames;
//     });
//   };

//   const handleNext = () => {
//     if (!isStepComplete()) {
//       setSkipModal(true);
//     } else {
//       setStep(prev => Math.min(prev + 1, totalSteps));
//     }
//   };

//   const handleSkipConfirm = () => {
//     setSkipModal(false);
//     setStep(prev => Math.min(prev + 1, totalSteps));
//   };

//   const handleSkipCancel = () => {
//     setSkipModal(false);
//   };

//   const renderStepContent = () => {
//     if (!user) return <div className="loading-message">Loading your profile...</div>;

//     switch(step) {
//       case 1:
//         return (
//           <div className="onboarding-step">
//             <h1 className="welcome-title">Welcome to Our Community! 👋</h1>
//             <div className="step-description">
//               <p>Let's get you set up. We'll start with some basic information to help others get to know you.</p>
//               <p><em>This information will be visible on your public profile.</em></p>
//             </div>
//             <ProfileBasics setProfileInfo={setProfileInfo} />
//           </div>
//         );

//       case 2:
//         return (
//           <div className="onboarding-step">
//             <h2 className="step-title">Your Local Area</h2>
//             <div className="step-description">
//               <p>Sharing your location helps us connect you with nearby members and relevant local groups.</p>
//               <p><em>We only store approximate location data and never share exact coordinates.</em></p>
//             </div>
//             <NearestLocationForm setLocationInfo={setLocationInfo} />
//           </div>
//         );

//       case 3:
//         return (
//           <div className="onboarding-step">
//             <h2 className="step-title">Profile Photos</h2>
//             <div className="step-description">
//               <p>Add photos to help others recognize you. The first photo will be your main profile image.</p>
//               <p><em>Please ensure photos clearly show your face and meet our community guidelines.</em></p>
//             </div>
//             <ProfilePicturesGrid setPhotosInfo={setPhotosInfo} />
//           </div>
//         );

//       default:
//         if (step > 3 && step <= totalCommunities + 3) {
//           const index = step - 4;
//           const community = user.listOfCommunities[index];
//           return (
//             <div className="onboarding-step">
//               <h2 className="step-title">
//                 {communityNames[index] || community?.name || `Community Profile`}
//               </h2>
//               <div className="step-description">
//                 <p>Customize your profile for this specific community. This information will only be visible to members of this group.</p>
//                 <p><em>You can update these settings anytime from your profile page.</em></p>
//               </div>
//               <CommunityProfileMain
//                 user={user}
//                 communityId={community._id || community}
//                 onCommunityName={(name) => handleCommunityName(index, name)}
//                 setCommunityProfileComplete={(complete) => {
//                   setCommunityProfileInfo(prev => {
//                     const updated = [...prev];
//                     updated[index] = complete;
//                     return updated;
//                   });
//                 }}
//               />
//             </div>
//           );
//         }

//         if (step === totalCommunities + 4) {
//           return (
//             <div className="onboarding-step">
//               <h2 className="step-title">Notification Preferences</h2>
//               <div className="step-description">
//                 <p>Choose how you'd like to receive important updates and community notifications.</p>
//                 <p><em>You can adjust these settings later in your account preferences.</em></p>
//               </div>
//               <Alerts onCheckboxChange={setIsChecked} />
//             </div>
//           );
//         }

//         if (step === totalCommunities + 5) {
//           return (
//             <div className="onboarding-step">
//               <h2 className="step-title">Final Settings</h2>
//               <div className="step-description">
//                 <p>Review and confirm your privacy preferences and community settings.</p>
//                 <p><em>These settings help us protect your privacy and customize your experience.</em></p>
//               </div>
//               <CommuniSettings
//                 user={user}
//                 communityId={user.listOfCommunities[totalCommunities - 1]}
//               />
//             </div>
//           );
//         }

//         if (step === totalSteps) {
//           return (
//             <div className="completion-screen">
//               <h1 className="congratulations-title">Setup Complete! 🎉</h1>
//               <div className="completion-message">
//                 <p>Welcome to our community! You're now ready to start connecting with others.</p>
//                 <p>Redirecting you to the main dashboard...</p>
//               </div>
//             </div>
//           );
//         }
//         return null;
//     }
//   };

//   return (
//     <div className="onboarding-container">
//       <div className="progress-container">
//         <div className="progress-text">
//           Step {Math.min(step, totalSteps)} of {totalSteps}
//         </div>
//         <div className="progress-bar">
//           <div 
//             className="progress-fill"
//             style={{ width: `${(step / totalSteps) * 100}%` }}
//           />
//         </div>
//       </div>

//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           timeout={300}
//           classNames="fade-step"
//         >
//           <div className="step-container">
//             {renderStepContent()}
//             {step < totalSteps && (
//               <div className="navigation-buttons">
//                 <button 
//                   className="next-button"
//                   onClick={handleNext}
//                 >
//                   {step === totalSteps - 1 ? 'Complete Setup' : 'Continue'}
//                 </button>
//               </div>
//             )}
//           </div>
//         </CSSTransition>
//       </SwitchTransition>

//       {/* Skip Confirmation Modal */}
//       {skipModal && (
//         <div className="confirmation-modal">
//           <div className="modal-content">
//             <h3>Incomplete Section</h3>
//             <p>You haven't completed this section. Would you like to:</p>
//             <div className="modal-actions">
//               <button className="secondary-button" onClick={handleSkipCancel}>
//                 Go Back and Complete
//               </button>
//               <button className="primary-button" onClick={handleSkipConfirm}>
//                 Skip for Now
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Email Alerts Check Modal */}
//       {showModal && (
//         <EmailAlertsCheck
//           onClose={() => setShowModal(false)}
//           onConfirm={() => {
//             setShowModal(false);
//             setStep(prev => prev + 1);
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default Onboarding;

// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import "./Settings.css";
// import "./ProfileUpdate.css";
// import "./Onboarding.css";

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [profileInfo, setProfileInfo] = useState(null);
//   const [locationInfo, setLocationInfo] = useState(null);
//   const [photosInfo, setPhotosInfo] = useState(null);
//   const [communityProfileInfo, setCommunityProfileInfo] = useState([]);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const [isChecked, setIsChecked] = useState(false);
//   const [showModal, setShowModal] = useState(false);

//   const { user } = useAuthContext();
//   const navigate = useNavigate();

//   const totalCommunities = user?.listOfCommunities?.length || 0;
//   const totalSteps = totalCommunities + 6;

//   // Completion tracking
//   const isStepComplete = useCallback(() => {
//     if (step === 1) return !!profileInfo;
//     if (step === 2) return !!locationInfo;
//     if (step === 3) return !!photosInfo;
//     if (step > 3 && step <= totalCommunities + 3) return !!communityProfileInfo[step - 4];
//     if (step === totalCommunities + 4) return isChecked;
//     return true;
//   }, [step, profileInfo, locationInfo, photosInfo, communityProfileInfo, totalCommunities, isChecked]);

//   // Section header component
//   const renderSectionHeader = (title, isComplete) => (
//     <div className="section-header">
//       <h2>
//         {isComplete ? (
//           <span role="img" aria-label="complete" className="checkmark">🟢</span>
//         ) : (
//           <span role="img" aria-label="incomplete" className="warning">🏗️</span>
//         )}
//         {title}
//       </h2>
//     </div>
//   );

//   // Navigation effects
//   useEffect(() => {
//     if (step === totalSteps) {
//       const timer = setTimeout(() => navigate('/tester1'), 6000);
//       return () => clearTimeout(timer);
//     }
//   }, [step, totalSteps, navigate]);

//   // Community handlers
//   const handleCommunityName = useCallback((idx, name) => {
//     setCommunityNames(prev => {
//       const updated = [...prev];
//       updated[idx] = name;
//       return updated;
//     });
//   }, []);

//   const handleCommunityComplete = useCallback((idx, complete) => {
//     setCommunityProfileInfo(prev => {
//       const updated = [...prev];
//       updated[idx] = complete;
//       return updated;
//     });
//   }, []);

//   // Step progression
//   const nextStep = useCallback(() => {
//     if (step === totalCommunities + 4 && !isChecked) {
//       setShowModal(true);
//     } else {
//       setStep(prev => Math.min(prev + 1, totalSteps));
//     }
//   }, [step, totalCommunities, isChecked, totalSteps]);

//   const handleModalConfirm = () => {
//     setShowModal(false);
//     nextStep();
//   };

//   // Render current step content
//   const renderStepContent = () => {
//     if (!user) return <div className="loading">Loading user data...</div>;

//     switch (step) {
//       case 1:
//         return (
//           <div className="onboarding-step">
//             {renderSectionHeader("Basic Information", profileInfo)}
//             <ProfileBasics setProfileInfo={setProfileInfo} />
//           </div>
//         );

//       case 2:
//         return (
//           <div className="onboarding-step">
//             {renderSectionHeader("Your Location", locationInfo)}
//             <NearestLocationForm setLocationInfo={setLocationInfo} />
//           </div>
//         );

//       case 3:
//         return (
//           <div className="onboarding-step">
//             {renderSectionHeader("Profile Photos", photosInfo)}
//             <ProfilePicturesGrid 
//               setPhotosInfo={setPhotosInfo}
//               setImageLinks={setImageLinks}
//             />
//           </div>
//         );

//       default:
//         if (step > 3 && step <= totalCommunities + 3) {
//           const idx = step - 4;
//           const community = user.listOfCommunities[idx];
//           const name = communityNames[idx] || community?.name || `Community ${idx + 1}`;
//           const complete = communityProfileInfo[idx];

//           return (
//             <div className="onboarding-step">
//               {renderSectionHeader(`${name} Profile`, complete)}
//               <CommunityProfileMain
//                 user={user}
//                 communityId={community?._id || community}
//                 onCommunityName={(name) => handleCommunityName(idx, name)}
//                 setCommunityProfileComplete={(complete) => handleCommunityComplete(idx, complete)}
//               />
//             </div>
//           );
//         }

//         if (step === totalCommunities + 4) {
//           return (
//             <div className="onboarding-step">
//               {renderSectionHeader("Notification Preferences", isChecked)}
//               <Alerts onCheckboxChange={setIsChecked} />
//             </div>
//           );
//         }

//         if (step === totalCommunities + 5) {
//           return (
//             <div className="onboarding-step">
//               {renderSectionHeader("Final Settings", true)}
//               <CommuniSettings
//                 user={user}
//                 communityId={user.listOfCommunities[totalCommunities - 1]}
//               />
//             </div>
//           );
//         }

//         if (step === totalSteps) {
//           return (
//             <div className="completion-screen">
//               <h1>🎉 Welcome to the Community!</h1>
//               <h2>Your profile is complete and ready to use</h2>
//               <p>Redirecting to your dashboard...</p>
//             </div>
//           );
//         }
//         return null;
//     }
//   };

//   return (
//     <div className="onboarding-container">
//       <div className="progress-header">
//         <div className="progress-text">
//           Step {Math.min(step, totalSteps - 1)} of {totalSteps - 1}
//         </div>
//         <div className="progress-bar">
//           <div 
//             className="progress-fill"
//             style={{ width: `${(step / (totalSteps - 1)) * 100}%` }}
//           />
//         </div>
//       </div>

//       {showModal && (
//         <EmailAlertsCheck
//           onClose={() => setShowModal(false)}
//           onConfirm={handleModalConfirm}
//         />
//       )}

//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           timeout={300}
//           classNames="fade-step"
//           unmountOnExit
//         >
//           <div className="step-content">
//             {renderStepContent()}
//             {step < totalSteps && (
//               <button 
//                 className={`next-button ${isStepComplete() ? 'active' : 'disabled'}`}
//                 onClick={nextStep}
//                 disabled={!isStepComplete()}
//               >
//                 {step === totalSteps - 1 ? 'Finish Setup' : 'Continue'}
//               </button>
//             )}
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// };

// export default Onboarding;


// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import "./Settings.css";
// import "./ProfileUpdate.css";
// import "./Onboarding.css";

// const Onboarding = () => {
//   const [step, setStep] = useState(1);

//   // Completion states for each section
//   const [profileInfo, setProfileInfo] = useState(null);
//   const [locationInfo, setLocationInfo] = useState(null);
//   const [photosInfo, setPhotosInfo] = useState(null);
//   const [communityProfileInfo, setCommunityProfileInfo] = useState([]);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const [isChecked, setIsChecked] = useState(false);
//   const [showModal, setShowModal] = useState(false);

//   const { user } = useAuthContext();
//   const navigate = useNavigate();

//   const totalCommunities = Array.isArray(user?.listOfCommunities) ? user.listOfCommunities.length : 0;
//   const totalSteps = totalCommunities + 6;

//   // Progress logic
//   const incompleteSections = useMemo(() => {
//     const sections = [];
//     if (!profileInfo) sections.push("Basic Info");
//     if (!locationInfo) sections.push("Location");
//     if (!photosInfo) sections.push("Photos");
//     if (Array.isArray(user?.listOfCommunities)) {
//       user.listOfCommunities.forEach((community, idx) => {
//         if (!communityProfileInfo[idx]) {
//           const name =
//             communityNames[idx] ||
//             community.name ||
//             community.communityName ||
//             `Community ${idx + 1}`;
//           sections.push(name);
//         }
//       });
//     }
//     return sections;
//   }, [
//     profileInfo,
//     locationInfo,
//     photosInfo,
//     communityProfileInfo,
//     user?.listOfCommunities,
//     communityNames,
//   ]);
//   const isStepComplete = useCallback(() => {
//     if (step === 1) return !!profileInfo;
//     if (step === 2) return !!locationInfo;
//     if (step === 3) return !!photosInfo;
//     if (step > 3 && step <= totalCommunities + 3) return !!communityProfileInfo[step - 4];
//     if (step === totalCommunities + 4) return isChecked;
//     if (step === totalCommunities + 5) return true;
//     return true;
//   }, [step, profileInfo, locationInfo, photosInfo, communityProfileInfo, totalCommunities, isChecked]);

//   // Navigation after completion
//   useEffect(() => {
//     if (step === totalSteps) {
//       const timer = setTimeout(() => navigate('/tester1'), 6000);
//       return () => clearTimeout(timer);
//     }
//   }, [step, totalSteps, navigate]);

//   // Community name handler
//   const handleCommunityName = useCallback((name, idx) => {
//     setCommunityNames(prev => {
//       const updated = [...prev];
//       updated[idx] = name;
//       return updated;
//     });
//   }, []);

//   // Community completion handler
//   const setCommunityProfileComplete = useCallback((complete, idx) => {
//     setCommunityProfileInfo(prev => {
//       const updated = [...prev];
//       updated[idx] = complete;
//       return updated;
//     });
//   }, []);

//   // Step progression logic
//   const nextStep = useCallback(() => {
//     if (step === totalCommunities + 4) {
//       if (!isChecked) {
//         setShowModal(true);
//       } else {
//         setStep(s => s + 1);
//       }
//     } else {
//       setStep(s => s + 1);
//     }
//   }, [step, totalCommunities, isChecked]);

//   const handleModalConfirm = () => {
//     setShowModal(false);
//     setStep(s => s + 1);
//   };

//   // Render step content
//   const renderStepContent = () => {
//     if (!user) return <div>Loading user data...</div>;
//     switch (step) {
//       case 1:
//         return (
//           <div>
//             <h1>Welcome!</h1>
//             <p>To get you set up, please complete the following:</p>
//             <ProfileBasics setProfileInfo={setProfileInfo} />
//           </div>
//         );
//       case 2:
//         return (
//           <div>
//             <p>Let us know where you are based to help find people nearby.</p>
//             <NearestLocationForm setLocationInfo={setLocationInfo} />
//           </div>
//         );
//       case 3:
//         return (
//           <div>
//             <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//             <ProfilePicturesGrid setPhotosInfo={setPhotosInfo} setImageLinks={setImageLinks} />
//           </div>
//         );
//       default:
//         // Community profile steps
//         if (step > 3 && step <= totalCommunities + 3) {
//           const idx = step - 4;
//           return (
//             <div>
//               <p>Fill in your community info for:</p>
//               <h1>{communityNames[idx] || user.listOfCommunities[idx]}</h1>
//               <CommunityProfileMain
//                 user={user}
//                 communityId={user.listOfCommunities[idx]?.toString()}
//                 onCommunityName={name => handleCommunityName(name, idx)}
//                 setCommunityProfileComplete={complete => setCommunityProfileComplete(complete, idx)}
//               />
//             </div>
//           );
//         }
//         // Alerts
//         if (step === totalCommunities + 4) {
//           return <Alerts onCheckboxChange={setIsChecked} />;
//         }
//         // Settings
//         if (step === totalCommunities + 5) {
//           const lastIdx = totalCommunities - 1;
//           return (
//             <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings
//                 user={user}
//                 communityId={user.listOfCommunities[lastIdx]?.toString()}
//                 onCommunityName={name => handleCommunityName(name, lastIdx)}
//               />
//             </div>
//           );
//         }
//         // Completion
//         if (step === totalSteps) {
//           return (
//             <div>
//               <h1>Your account is complete! Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>
//             </div>
//           );
//         }
//         return null;
//     }
//   };

//   return (
//     <div>
//       <div
//         className="profile-completeness-alert"
//         style={{
//           background: incompleteSections.length === 0 ? "#e6faea" : "#fff3cd",
//           color: incompleteSections.length === 0 ? "#059669" : "#b45309",
//           borderRadius: "1rem",
//           padding: "1.1rem 1.5rem",
//           marginBottom: "1.4rem",
//           boxShadow: "0 1px 8px 0 rgba(99,102,241,0.04)"
//         }}
//       >
//         {incompleteSections.length === 0 ? (
//           <span>
//             <span role="img" aria-label="complete" style={{fontSize: "1.1em", marginRight: "0.5em"}}>🟢</span>
//             Your onboarding is complete!
//           </span>
//         ) : (
//           <span>
//             <span role="img" aria-label="incomplete" style={{fontSize: "1.2em", marginRight: "0.5em"}}>🏗️</span>
//             <strong>Please complete:</strong>{" "}
//             {incompleteSections.map((section, idx) => (
//               <span key={section}>
//                 {section}{idx < incompleteSections.length - 1 ? ", " : ""}
//               </span>
//             ))}
//           </span>
//         )}
//       </div>
//       {showModal && (
//         <EmailAlertsCheck
//           onClose={() => setShowModal(false)}
//           onConfirm={handleModalConfirm}
//         />
//       )}
//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           timeout={300}
//           classNames="fade"
//         >
//           <div>
//             {renderStepContent()}
//             {step < totalSteps && (
//               <button onClick={nextStep} disabled={!isStepComplete()}>
//                 Next Step
//               </button>
//             )}
//             {step < totalSteps - 1 && (
//               <p>Progress: {step}/{totalSteps - 1}</p>
//             )}
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// };

// export default Onboarding;


// import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import "./Settings.css";
// import "./ProfileUpdate.css";
// import "./Onboarding.css";

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [isChecked, setIsChecked] = useState(false);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const { user } = useAuthContext();
//   const navigate = useNavigate();
//   const [locationInfo, setLocationInfo] = useState(null);
  

//   // Calculate total steps based on user communities
//   const totalSteps = (user?.listOfCommunities?.length || 0) + 6;

//   // Handle navigation after completion
//   useEffect(() => {
//     if (step === totalSteps) {
//       const timer = setTimeout(() => navigate('/tester1'), 6000);
//       return () => clearTimeout(timer);
//     }
//   }, [step, totalSteps, navigate]);

//   // Handle community name updates
//   const handleCommunityName = useCallback((name, idx) => {
//     setCommunityNames(prev => {
//       const updated = [...prev];
//       updated[idx] = name;
//       return updated;
//     });
//   }, []);

//   // Handle image links update
//   const handleImageLinksUpdate = useCallback((links) => setImageLinks(links), []);

//   // Step progression logic
//   const nextStep = useCallback(() => {
//     if (step === (user?.listOfCommunities?.length || 0) + 4) {
//       if (!isChecked) {
//         setShowModal(true);
//       } else {
//         setStep(s => s + 1);
//       }
//     } else {
//       setStep(s => s + 1);
//     }
//   }, [step, user, isChecked]);

//   // Modal confirmation
//   const handleModalConfirm = () => {
//     setShowModal(false);
//     setStep(s => s + 1);
//   };

//   // Render step content
//   const renderStepContent = () => {
//     if (!user) return <div>Loading user data...</div>;

//     switch (step) {
//       case 1:
//         return (
//           <div>
//             <h1>Welcome!</h1>
//             <p>To get you set up, please complete the following:</p>
//             <ProfileBasics />
//           </div>
//         );
//       case 2:
//         return (
//           <div>
//             <p>Let us know where you are based to help find people nearby.</p>
//             <NearestLocationForm />
//           </div>
//         );
//       case 3:
//         return (
//           <div>
//             <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//             <ProfilePicturesGrid onImageLinksUpdate={handleImageLinksUpdate} />
//           </div>
//         );
//       default:
//         // Community profile steps
//         if (
//           step > 3 &&
//           step <= (user.listOfCommunities?.length || 0) + 3
//         ) {
//           const idx = step - 4;
//           return (
//             <div>
//               <p>Fill in your community info for:</p>
//               <h1>{communityNames[idx] || user.listOfCommunities[idx]}</h1>
//               <CommunityProfileMain
//                 user={user}
//                 communityId={user.listOfCommunities[idx]?.toString()}
//                 onCommunityName={name => handleCommunityName(name, idx)}
//               />
//             </div>
//           );
//         }
//         // Alerts
//         if (step === (user.listOfCommunities?.length || 0) + 4) {
//           return <Alerts onCheckboxChange={setIsChecked} />;
//         }
//         // Settings
//         if (step === (user.listOfCommunities?.length || 0) + 5) {
//           const lastIdx = (user.listOfCommunities?.length || 1) - 1;
//           return (
//             <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings
//                 user={user}
//                 communityId={user.listOfCommunities[lastIdx]?.toString()}
//                 onCommunityName={name => handleCommunityName(name, lastIdx)}
//               />
//             </div>
//           );
//         }
//         // Completion
//         if (step === totalSteps) {
//           return (
//             <div>
//               <h1>Your account is complete! Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>
//             </div>
//           );
//         }
//         return null;
//     }
//   };

//   return (
//     <div>
//       {showModal && (
//         <EmailAlertsCheck
//           onClose={() => setShowModal(false)}
//           onConfirm={handleModalConfirm}
//         />
//       )}
//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           timeout={300}
//           classNames="fade"
//         >
//           <div>
//             {renderStepContent()}
//             {step < totalSteps && (
//               <button onClick={nextStep}>Next Step</button>
//             )}
//             {step < totalSteps - 1 && (
//               <p>Progress: {step}/{totalSteps - 1}</p>
//             )}
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// };

// export default Onboarding;



// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import EmailAlertsCheck from '../components/Modal/EmailAlertsCheck';
// import "./Settings.css";
// import "./ProfileUpdate.css"; 
// import "./Onboarding.css"; 
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicProfileSeperatePopUpEditcopy from "../components/Profile&Settings/DynamicProfileSeperatePopUpEditcopy";
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';
// import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [isChecked, setIsChecked] = useState(false);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const { user } = useAuthContext();
//   const navigate = useNavigate();
//   const [communityProfileInfo, setCommunityProfileInfo] = useState([]);

  

  
//   useEffect(() => {
//     console.log('onboarding happening and user is', user);
//   }, [user]); 

//   console.log('onboarding happening and user is', user)

//   const nextStep = () => {

    
//     if (step === user.listOfCommunities.length + 4) {
//       if (!isChecked) {
//         setShowModal(true);
//       } else {
//         setStep(prevStep => prevStep + 1);
//       }
//     } else {
//       setStep(prevStep => prevStep + 1);
//     }
//   };

//   useEffect(() => {
//     if (step === user.listOfCommunities.length + 6) {
//       setTimeout(() => {
//         navigate('/tester1');
//       }, 6000); // 6000 milliseconds = 6 seconds
//     }
//   }, [step]);

//   const handleCommunityName = (communityName, index) => {
//     setCommunityNames(prevNames => {
//       const newNames = [...prevNames];
//       newNames[index] = communityName;
//       return newNames;
//     });
//   };

//   const handleImageLinksUpdate = (links) => {
//     setImageLinks(links);
//   };

//   useEffect(() => {
//     // Simulate the loading of components
//     setTimeout(() => {
//       setStep(1);
//     }, 4000);
//   }, []);

//   return (
//     <div>
//       {showModal && (
//         <EmailAlertsCheck 
//           onClose={() => setShowModal(false)}
//           onConfirm={() => {
//             setShowModal(false);
//             setStep(prevStep => prevStep + 1);
//           }}
//         />
//       )}
//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           addEndListener={(node, done) => {
//             node.addEventListener("transitionend", done, false);
//           }}
//           classNames='fade'
//         >
//           <div>
//             {step === 1 && <div>
//               <h1>Welcome!</h1>
//               <p>To get you set up, please complete the following:</p>
//               <ProfileBasics />
//             </div>}
//             {step === 2 && <div>
//               <p>Let us know where you are based to help find people nearby.</p>
//               <NearestLocationForm />
//             </div>}
//             {step === 3 && <div>
//               <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//               <ProfilePicturesGrid onImageLinksUpdate={handleImageLinksUpdate} />
//             </div>}
//             {Array.isArray(user.listOfCommunities) && user.listOfCommunities.map((community, index) => (
//               step === index + 4 && <div>
//                 <p>Fill in your community info for: </p>
//                 <h1>{communityNames[index]}</h1>
//                 <CommunityProfileMain
//                   user={user}
//                   communityId={community.toString()}
//                   onCommunityName={(name) => handleCommunityName(name, index)}
//                   setCommunityProfileComplete={setCommunityProfileInfo}
//                 />
//               </div>
//             ))}
//             {step === user.listOfCommunities.length + 4 && <div>
//               {/* <p>And finally, set your alerts!</p> */}
//               <Alerts onCheckboxChange={setIsChecked} />
//             </div>}
//             {step === user.listOfCommunities.length + 5 && <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings 
//                 user={user}
//                 communityId={user.listOfCommunities[user.listOfCommunities.length - 1].toString()}
//                 onCommunityName={(name) => handleCommunityName(name, user.listOfCommunities.length - 1)}
//               />
//             </div>}
//             {step === user.listOfCommunities.length + 6 && <div>
//               <h1>Your account is complete!  Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>
//             </div>}
//             <div>
//               {step < user.listOfCommunities.length + 6 && <button onClick={nextStep}>Next Step</button>}
//               {step < user.listOfCommunities.length + 5 && <p>Progress: {step}/{user.listOfCommunities.length + 5}</p>}
//             </div>
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// }

// export default Onboarding;



       



// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import "./Settings.css";
// import "./ProfileUpdate.css"; 
// import "./Onboarding.css"; 
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicProfileSeperatePopUpEditcopy from "../components/Profile&Settings/DynamicProfileSeperatePopUpEditcopy";
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [isChecked, setIsChecked] = useState(false);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const { user } = useAuthContext();
//   const navigate = useNavigate();
  
//   const nextStep = () => {
//     if (step === user.listOfCommunities.length + 4) {
//       if (!isChecked) {
//                 if (window.confirm("Are you sure?")) {
//                   setStep(prevStep => prevStep + 1);
//                 }
//       } else {
//         setStep(prevStep => prevStep + 1);
//       }
//     } else {
//       setStep(prevStep => prevStep + 1);
//     }
//   };

//   const handleCommunityName = (communityName, index) => {
//     setCommunityNames(prevNames => {
//       const newNames = [...prevNames];
//       newNames[index] = communityName;
//       return newNames;
//     });
//   };

//   const handleImageLinksUpdate = (links) => {
//     setImageLinks(links);
//   };

//   useEffect(() => {
//     // Simulate the loading of components
//     setTimeout(() => {
//       setStep(1);
//     }, 4000);
//   }, []);

//   return (
//     <div>
//       <SwitchTransition>
//         <CSSTransition
//           key={step}
//           addEndListener={(node, done) => {
//             node.addEventListener("transitionend", done, false);
//           }}
//           classNames='fade'
//         >
//           <div>
//             {step === 1 && <div>
//               <h1>Welcome!</h1>
//               <p>To get you set up, please complete the following:</p>
//               <ProfileBasics />
//             </div>}
//             {step === 2 && <div>
//               <p>Let us know where you are based to help find people nearby.</p>
//               <NearestLocationForm />
//             </div>}
//             {step === 3 && <div>
//               <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//               <ProfilePicturesGrid onImageLinksUpdate={handleImageLinksUpdate} />
//             </div>}
//             {Array.isArray(user.listOfCommunities) && user.listOfCommunities.map((community, index) => (
//               step === index + 4 && <div>
//                 <p>Fill in your community info for: </p>
//                 <h1>{communityNames[index]}</h1>
//                 <DynamicProfileSeperatePopUpEditcopy
//                   user={user}
//                   communityId={community.toString()}
//                   onCommunityName={(name) => handleCommunityName(name, index)}
//                 />
//               </div>
//             ))}
//             {step === user.listOfCommunities.length + 4 && <div>
//               <p>And finally, set your alerts!</p>
//               <Alerts onCheckboxChange={setIsChecked} />
//             </div>}
//             {step === user.listOfCommunities.length + 5 && <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings 
//                 user={user}
//                 communityId={user.listOfCommunities[user.listOfCommunities.length - 1].toString()}
//                 onCommunityName={(name) => handleCommunityName(name, user.listOfCommunities.length - 1)}
//               />
//             </div>}
//             {step === user.listOfCommunities.length + 6 && <div>
//               <h1>Your account is complete!  Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>
//             </div>}
//             <div>
//               {step < user.listOfCommunities.length + 6 && <button onClick={nextStep}>Next Step</button>}
//               {step < user.listOfCommunities.length + 5 && <p>Progress: {step}/{user.listOfCommunities.length + 5}</p>}
//             </div>
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// }

// export default Onboarding;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import "./Settings.css";
// import "./ProfileUpdate.css"; 
// import "./Onboarding.css"; 
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicProfileSeperatePopUpEditcopy from "../components/Profile&Settings/DynamicProfileSeperatePopUpEditcopy";
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import Alerts from '../components/Profile&Settings/AlertsSettings';
// import { useAuthContext } from '../hooks/useAuthContext';

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [isChecked, setIsChecked] = useState(false); // Add this line
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const { user } = useAuthContext();
//   const navigate = useNavigate();
  
//   const nextStep = () => {
//     if (step === user.listOfCommunities.length + 4) {
//       if (!isChecked) {
//         if (window.confirm("Are you sure?")) {
//           setStep(prevStep => prevStep + 1);
//         }
//       } else {
//         setStep(prevStep => prevStep + 1);
//       }
//     } else {
//       setStep(prevStep => prevStep + 1);
//     }
//   };


//   const handleCommunityName = (communityName, index) => {
//     setCommunityNames(prevNames => {
//       const newNames = [...prevNames];
//       newNames[index] = communityName;
//       return newNames;
//     });
//   };

//   const handleImageLinksUpdate = (links) => {
//     setImageLinks(links);
//   };

//   useEffect(() => {
//     // Simulate the loading of components
//     setTimeout(() => {
//       setStep(1);
//     }, 4000);
//   }, []);

//   useEffect(() => {
//     if (step === user.listOfCommunities.length + 5) {
//       setTimeout(() => {
//         navigate('/tester1');
//       }, 4000);
//     }
//   }, [step, user.listOfCommunities.length, navigate]);

//   return (
//     <div>
//       <SwitchTransition>
//         <CSSTransition
//           key={step} // Updated key
//           addEndListener={(node, done) => {
//             node.addEventListener("transitionend", done, false);
//           }}
//           classNames='fade'
//         >
//           <div>
//             {step === 1 && <div>
//               <h1>Welcome!</h1>
//               <p>To get you set up, please complete the following:</p>
//               <ProfileBasics />
//             </div>}
//             {step === 2 && <div>
//               <p>Let us know where you are based to help find people nearby.</p>
//               <NearestLocationForm />
//             </div>}
//             {step === 3 && <div>
//               <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//               <ProfilePicturesGrid onImageLinksUpdate={handleImageLinksUpdate} />
//             </div>}
//             {Array.isArray(user.listOfCommunities) && user.listOfCommunities.map((community, index) => (
//               step === index + 4 && <div>
//                 <p>Fill in your community info for: </p>
//                 <h1>{communityNames[index]}</h1>

//                 <DynamicProfileSeperatePopUpEditcopy
//                   user={user}
//                   communityId={community.toString()}
//                   onCommunityName={(name) => handleCommunityName(name, index)}
//                 />
//               </div>
//             ))}
//             {step === user.listOfCommunities.length + 4 && <div>
//             <p>And finally, set your alerts!</p>
//             <Alerts onCheckboxChange={setIsChecked} />
//           </div>}
//             {step === user.listOfCommunities.length + 5 && <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings 
//                 user={user}
//                 communityId={user.listOfCommunities[user.listOfCommunities.length - 1].toString()}
//                 onCommunityName={(name) => handleCommunityName(name, user.listOfCommunities.length - 1)}
//               />
//             </div>}
//             {step === user.listOfCommunities.length + 6 && <div>
//               <h1>Your account is complete!  Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>

//             </div>}
//             <div>
//               {step < user.listOfCommunities.length + 5 && <button onClick={nextStep}>Next Step</button>}
//               {step < user.listOfCommunities.length + 4 && <p>Progress: {step}/{user.listOfCommunities.length + 4}</p>}
//             </div>
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// }

// export default Onboarding;


// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
// import "./Settings.css";
// import "./ProfileUpdate.css"; 
// import "./Onboarding.css"; 
// import ProfileBasics from "../components/Profile&Settings/ProfileBasics";
// import NearestLocationForm from '../components/Location/NearestLocationField';
// import ProfilePicturesGrid from '../components/Uploads/ProfilePhotoUpload';
// import DynamicProfileSeperatePopUpEditcopy from "../components/Profile&Settings/DynamicProfileSeperatePopUpEditcopy";
// import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
// import { useAuthContext } from '../hooks/useAuthContext';
// import Alerts from '../components/Profile&Settings/AlertsSettings';

// const Onboarding = () => {
//   const [step, setStep] = useState(1);
//   const [communityNames, setCommunityNames] = useState([]);
//   const [imageLinks, setImageLinks] = useState([]);
//   const { user } = useAuthContext();
//   const navigate = useNavigate();

//   const nextStep = () => {
//     setStep(prevStep => prevStep + 1);
//   };

//   const handleCommunityName = (communityName, index) => {
//     setCommunityNames(prevNames => {
//       const newNames = [...prevNames];
//       newNames[index] = communityName;
//       return newNames;
//     });
//   };

//   const handleImageLinksUpdate = (links) => {
//     setImageLinks(links);
//   };

//   useEffect(() => {
//     // Simulate the loading of components
//     setTimeout(() => {
//       setStep(1);
//     }, 4000);
//   }, []);

//   useEffect(() => {
//     if (step === user.listOfCommunities.length + 5) {
//       setTimeout(() => {
//         navigate('/tester1');
//       }, 4000);
//     }
//   }, [step, user.listOfCommunities.length, navigate]);

//   return (
//     <div>
//       <SwitchTransition>
//         <CSSTransition
//           key={step} // Updated key
//           addEndListener={(node, done) => {
//             node.addEventListener("transitionend", done, false);
//           }}
//           classNames='fade'
//         >
//           <div>
//             {step === 1 && <div>
//               <h1>Welcome!</h1>
//               <p>To get you set up, please complete the following:</p>
//               <ProfileBasics />
//             </div>}
//             {step === 2 && <div>
//               <p>Let us know where you are based to help find people nearby.</p>
//               <NearestLocationForm />
//             </div>}
//             {step === 3 && <div>
//               <p>Please upload a few pictures of yourself. The first will be your profile image.</p>
//               <ProfilePicturesGrid onImageLinksUpdate={handleImageLinksUpdate} />
//             </div>}
//             {Array.isArray(user.listOfCommunities) && user.listOfCommunities.map((community, index) => (
//               step === index + 4 && <div>
//                 <p>Fill in your community info for: </p>
//                 <h1>{communityNames[index]}</h1>

//                 <DynamicProfileSeperatePopUpEditcopy
//                   user={user}
//                   communityId={community.toString()}
//                   onCommunityName={(name) => handleCommunityName(name, index)}
//                 />
//               </div>
//             ))}
//             {step === user.listOfCommunities.length + 4 && <div>
//               <p>And finally, set your settings!</p>
//               <CommuniSettings 
//                 user={user}
//                 communityId={user.listOfCommunities[user.listOfCommunities.length - 1].toString()}
//                 onCommunityName={(name) => handleCommunityName(name, user.listOfCommunities.length - 1)}
//               />
//             </div>}
//             {step === user.listOfCommunities.length + 5 && <div>
//               <h1>Your account is complete!  Welcome to the Interconnector</h1>
//               <h2>You'll now be taken to your messages where you can connect with people</h2>

//             </div>}
//             <div>
//               {step < user.listOfCommunities.length + 5 && <button onClick={nextStep}>Next Step</button>}
//               {step < user.listOfCommunities.length + 4 && <p>Progress: {step}/{user.listOfCommunities.length + 4}</p>}
//             </div>
//           </div>
//         </CSSTransition>
//       </SwitchTransition>
//     </div>
//   );
// }

// export default Onboarding;